export default {
  name: "ColumnVisibilitySelector",
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    storageKey: {
      type: String,
      default: ""
    },
    contextKey: {
      type: [String, Number],
      default: "global"
    },
    buttonLabel: {
      type: String,
      default: "Spalten"
    },
    minSelected: {
      type: Number,
      default: 1
    },
    buttonColor: {
      type: String,
      default: "#00636C"
    }
  },
  data() {
    return {
      menuOpen: false,
      restoring: false
    };
  },
  computed: {
    normalizedOptions() {
      return (this.options || [])
        .map(option => ({
          text: option?.text || option?.value || "-",
          value: option?.value
        }))
        .filter(option => String(option.value || "").trim().length > 0);
    },
    selectedValues() {
      return this.sanitizeSelection(this.value);
    },
    resolvedStorageKey() {
      if (!this.storageKey) return "";
      const context = String(this.contextKey || "global").trim() || "global";
      return `${this.storageKey}:${context}`;
    }
  },
  watch: {
    contextKey() {
      this.restoreSelection();
    },
    options: {
      deep: true,
      handler() {
        this.ensureValidSelection();
      }
    },
    value: {
      deep: true,
      handler(nextValue) {
        if (this.restoring) return;
        const sanitized = this.sanitizeSelection(nextValue);
        this.persistSelection(sanitized);
      }
    }
  },
  mounted() {
    this.restoreSelection();
  },
  methods: {
    arraysEqual(a = [], b = []) {
      if (a.length !== b.length) return false;
      return a.every((entry, index) => entry === b[index]);
    },
    sanitizeSelection(value) {
      if (!Array.isArray(value)) return [];

      const selectedSet = new Set(value);
      return this.normalizedOptions
        .map(option => option.value)
        .filter(optionValue => selectedSet.has(optionValue));
    },
    emitSelection(nextValues) {
      const sanitized = this.sanitizeSelection(nextValues);
      if (this.arraysEqual(sanitized, this.selectedValues)) return;
      this.$emit("input", sanitized);
    },
    ensureValidSelection() {
      const sanitized = this.sanitizeSelection(this.value);
      if (sanitized.length >= this.minSelected) {
        this.emitSelection(sanitized);
        return;
      }

      const fallback = this.normalizedOptions
        .slice(0, this.minSelected)
        .map(option => option.value);
      this.emitSelection(fallback);
    },
    restoreSelection() {
      if (!this.normalizedOptions.length) {
        this.emitSelection([]);
        return;
      }

      this.restoring = true;
      try {
        if (!this.resolvedStorageKey) {
          this.ensureValidSelection();
          return;
        }

        const rawValue = window.localStorage.getItem(this.resolvedStorageKey);
        if (!rawValue) {
          this.ensureValidSelection();
          return;
        }

        const parsed = JSON.parse(rawValue);
        const restored = this.sanitizeSelection(parsed);

        if (restored.length >= this.minSelected) {
          this.emitSelection(restored);
          return;
        }

        this.ensureValidSelection();
      } catch (e) {
        this.ensureValidSelection();
      } finally {
        this.restoring = false;
      }
    },
    persistSelection(selection) {
      if (!this.resolvedStorageKey) return;
      try {
        window.localStorage.setItem(
          this.resolvedStorageKey,
          JSON.stringify(selection)
        );
      } catch (e) {
        // Ignore storage issues (private mode / quota exceeded).
      }
    },
    isVisible(columnKey) {
      return this.selectedValues.includes(columnKey);
    },
    isLastSelected(columnKey) {
      return (
        this.selectedValues.length <= this.minSelected &&
        this.selectedValues.includes(columnKey)
      );
    },
    toggleColumnVisibility(columnKey, isChecked) {
      const current = [...this.selectedValues];
      const isVisible = current.includes(columnKey);

      if (isChecked) {
        if (isVisible) return;
        const next = this.sanitizeSelection([...current, columnKey]);
        this.emitSelection(next);
        this.persistSelection(next);
        return;
      }

      if (!isVisible) return;
      const next = current.filter(value => value !== columnKey);
      if (next.length < this.minSelected) return;

      const sanitized = this.sanitizeSelection(next);
      this.emitSelection(sanitized);
      this.persistSelection(sanitized);
    }
  }
};
